import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XPFloat, ConfettiBurst } from "@/utils/animations";
import { PetAvatar } from "@/components/PetAvatar";
import { playXP, playLevelUp, playClick } from "@/utils/sounds";
import { EVOLUTION_STAGES, STAR_REWARDS, getStageFromStars, starsToNextStage } from "@/constants/petConstants";
import { DoodleHeart, DoodleStar } from "@/components/Doodles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Legacy XP — kept for existing XP display, not used for evolution
const LEVELS = [0, 10, 30, 60, 120, 220, 380, 600, 900, 1400];
const XP_FOR_SCORE = { 4: 20, 3: 15, 2: 10, 1: 5, 0: 2 };

const HEADINGS = {
  4: { main: "PERFECT! 🌟",   sub: (_, name) => `${name} is SO proud of you!` },
  3: { main: "AMAZING!",      sub: (_, name) => `Great effort! ${name} knew you could do it!` },
  2: { main: "Great job!",    sub: (_, name) => `Keep going! Every try makes you stronger!` },
  1: { main: "Good try!",     sub: (_, name) => `Keep going! Every try makes you stronger!` },
  0: { main: "Keep going!",   sub: (_, name) => `Keep going! Every try makes you stronger!` },
};

// ── Evolution overlay (shown when pet evolves) ─────────────────
function EvolutionOverlay({ petType, petName, oldStage, newStage, onDismiss }) {
  const stageName = EVOLUTION_STAGES[newStage - 1]?.name || 'Curious';
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(10,10,30,0.88)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 16, padding: 32,
      }}
    >
      {/* Glowing pet */}
      <motion.div
        animate={{ filter: ['drop-shadow(0 0 8px rgba(255,215,64,0.5))', 'drop-shadow(0 0 28px rgba(255,215,64,1))', 'drop-shadow(0 0 8px rgba(255,215,64,0.5))'] }}
        transition={{ duration: 1.2, repeat: Infinity }}
        style={{ marginBottom: 8 }}
      >
        <PetAvatar petType={petType} stage={newStage} mood="celebrating" size="xl" animated/>
      </motion.div>

      {/* Name badge */}
      <div style={{ background: '#FFD740', color: '#1A1A2E', borderRadius: 20, padding: '4px 18px', fontSize: 18, fontFamily: "'Patrick Hand', cursive", fontWeight: 400 }}>
        {petName}
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6 }}
        style={{ textAlign: 'center' }}
      >
        <div style={{ fontSize: 28, fontWeight: 600, color: '#FFD700', marginBottom: 4 }}>
          ✨ {petName} evolved!
        </div>
        <div style={{ fontSize: 16, color: '#A0A0D0', marginBottom: 16 }}>
          Stage {oldStage} → Stage {newStage}: <span style={{ color: '#FFD700', fontWeight: 600 }}>{stageName}</span>
        </div>
      </motion.div>

      <button
        onClick={onDismiss}
        style={{
          background: '#448AFF',
          color: '#fff', border: 'none', borderRadius: 12,
          padding: '12px 32px', fontSize: 18, fontFamily: "'Patrick Hand', cursive", fontWeight: 400, cursor: 'pointer',
        }}
      >
        Incredible! 🎉
      </button>
    </motion.div>
  );
}

export const Celebration = ({ topic, score, totalQuestions = 4, petType, petName, cliffhangerQuestion, nextTopic, onNext, userEmail }) => {
  const xpEarned   = XP_FOR_SCORE[score] ?? 2;
  const oldXP      = parseInt(localStorage.getItem('you_total_xp') || '0');
  const oldLevel   = parseInt(localStorage.getItem('you_current_level') || '0');
  const streak     = parseInt(localStorage.getItem('you_streak_count') || '0');
  const newXP      = oldXP + xpEarned;
  const newLevel   = LEVELS.reduce((lvl, threshold, i) => newXP >= threshold ? i : lvl, 0);
  const didLevelUp = newLevel > oldLevel;

  // Star system — local calculation so anonymous users see rewards too
  const SCORE_STARS = { [totalQuestions]: 5, [totalQuestions - 1]: 3, [totalQuestions - 2]: 1 };
  const starsEarned = SCORE_STARS[score] ?? 0;
  const oldStars    = parseInt(localStorage.getItem('you_pet_stars') || '0');
  const oldStage    = parseInt(localStorage.getItem('you_pet_stage') || '1');
  const newStars    = oldStars + starsEarned;
  const newStage    = getStageFromStars(newStars);
  const didEvolve   = newStage > oldStage;
  const starsLeft   = starsToNextStage(newStars);

  const stageName   = EVOLUTION_STAGES[newStage - 1]?.name || 'Baby';
  const heading     = HEADINGS[score] ?? HEADINGS[0];
  const cliffText   = cliffhangerQuestion || '';

  const [showEvo, setShowEvo] = useState(false);

  useEffect(() => {
    // Update localStorage
    localStorage.setItem('you_total_xp',      String(newXP));
    localStorage.setItem('you_current_level', String(newLevel));
    localStorage.setItem('you_pet_stars',     String(newStars));
    localStorage.setItem('you_pet_stage',     String(newStage));
    localStorage.setItem('you_last_session_at', new Date().toISOString());

    // Scroll to top so heading is visible
    window.scrollTo(0, 0);

    // Sounds
    if (didEvolve) { playLevelUp(); setTimeout(() => setShowEvo(true), 600); }
    else if (didLevelUp) { playLevelUp(); }
    else { playXP(); }

    // Backend: add stars
    if (starsEarned > 0 && userEmail) {
      fetch(`${API}/pet/add-stars`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail, stars: starsEarned, reason: 'quiz' }),
      }).catch(() => {});
    }
    // Backend: legacy XP
    if (userEmail) {
      fetch(`${API}/pet/add-xp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail, xp_amount: xpEarned }),
      }).catch(() => {});
    }
    // Backend: update dream diary with quiz score (non-blocking)
    const dreamId = localStorage.getItem('you_current_dream_id');
    const dreamDate = localStorage.getItem('you_current_dream_date');
    if (userEmail && dreamId && dreamDate) {
      fetch(`${API}/dream-diary/update-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail, dream_id: dreamId,
          dream_date: dreamDate, quiz_score: score, stars_earned: starsEarned,
        }),
      }).catch(() => {});
      localStorage.removeItem('you_current_dream_id');
      localStorage.removeItem('you_current_dream_date');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWhatsApp = () => {
    playClick();
    const msg = `I scored ${score}/${totalQuestions} on ${topic} on YOU! 🎉\n${petName} is ${stageName}!\nLearn through stories: https://youinfotainment.com`;
    window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank');
  };

  return (
    <>
      <AnimatePresence>
        {showEvo && (
          <EvolutionOverlay
            petType={petType} petName={petName}
            oldStage={oldStage} newStage={newStage}
            onDismiss={() => setShowEvo(false)}
          />
        )}
      </AnimatePresence>

      <div style={{ background: '#FFFFFF', border: '1.5px dashed #CCCCCC', borderRadius: 4, overflow: 'hidden', width: '100%', position: 'relative' }}>
        <style>{`
          @keyframes xpFloat {
            0%   { opacity: 1; transform: translateY(0) scale(1); }
            100% { opacity: 0; transform: translateY(-32px) scale(1.1); }
          }
        `}</style>

        {/* Confetti layer */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
          <ConfettiBurst trigger={true} colors={['#FFD740', '#448AFF', '#66BB6A', '#4ECDC4']} />
        </div>

        {/* Doodle decorations */}
        <div style={{ position: 'absolute', top: 12, right: 12, pointerEvents: 'none', zIndex: 20 }}>
          <DoodleStar color="#FFD740" opacity={0.2} size={10} />
        </div>
        <div style={{ position: 'absolute', bottom: 60, left: 12, pointerEvents: 'none', zIndex: 20 }}>
          <DoodleHeart color="#FF6B6B" opacity={0.2} size={12} />
        </div>

        <div style={{ padding: '16px 14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* 1. Stars earned animation */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ display: 'flex', gap: 2 }}>
                {Array.from({ length: totalQuestions }, (_, i) => (
                  <span key={i} style={{ fontSize: 28, opacity: i < score ? 1 : 0.2 }}>⭐</span>
                ))}
              </div>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#2D3436' }}>{score} / {totalQuestions}</span>
            </div>
            {starsEarned > 0 && (
              <div style={{ fontSize: 16, fontWeight: 600, color: '#448AFF' }}>
                +{starsEarned} ⭐ earned!
              </div>
            )}
            <div style={{ position: 'relative', height: 4 }}>
              <XPFloat amount={xpEarned} />
            </div>
          </div>

          {/* 2. PET AVATAR — large, central, emotional reward */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <PetAvatar petType={petType} stage={newStage} mood="celebrating" size={120} animated/>
              {/* Stars flying into pet */}
              {starsEarned > 0 && Array.from({ length: starsEarned }, (_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 1, y: 0, scale: 1.2 }}
                  animate={{ opacity: 0, y: -40, scale: 0.6 }}
                  transition={{ duration: 1.2, ease: 'easeOut', delay: i * 0.3 }}
                  style={{ position: 'absolute', top: 0, left: '50%', fontSize: 16 }}
                >⭐</motion.div>
              ))}
            </div>

            <p style={{ fontSize: 22, fontFamily: "'Patrick Hand', cursive", fontWeight: 400, color: '#448AFF', margin: 0, textAlign: 'center' }}>
              {heading.main}
            </p>
            <p style={{ fontSize: 15, color: '#636E72', margin: 0, textAlign: 'center' }}>
              {heading.sub(topic, petName)}
            </p>

            {/* Star progress bar */}
            <div style={{ width: '100%', maxWidth: 280 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <p style={{ fontSize: 11, color: '#666666', margin: 0 }}>{petName} · {stageName}</p>
                <p style={{ fontSize: 11, color: '#666666', margin: 0 }}>
                  {newStars} / {newStars + starsLeft} ⭐
                </p>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: '#F5F5F5', border: '1px dashed #DDDDDD', overflow: 'hidden' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${starsLeft > 0 ? Math.round((newStars / (newStars + starsLeft)) * 100) : 100}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  style={{ height: '100%', borderRadius: 3, background: '#448AFF' }}
                />
              </div>
              <p style={{ fontSize: 11, color: starsLeft <= 4 ? '#448AFF' : '#666666', margin: '4px 0 0', fontWeight: starsLeft <= 4 ? 600 : 400, textAlign: 'center' }}>
                {starsLeft > 0 ? `${starsLeft} more ⭐ to next evolution!` : `${petName} reached Legendary! 🌟`}
              </p>
            </div>
          </div>

          {/* 3. Stats cards row */}
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { emoji: '⭐', value: `+${starsEarned}`, label: 'Stars', accent: '#FFD740', bg: '#FFF9E6', delay: 0 },
              { emoji: '🔥', value: `${streak}d`, label: 'Streak', accent: '#FF6B6B', bg: '#FFF0F0', delay: 0.1 },
              { emoji: '✨', value: stageName, label: `Stage ${newStage}`, accent: '#AB47BC', bg: '#F0E8FF', delay: 0.2 },
            ].map((card, i) => (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: card.delay, type: 'spring', stiffness: 300, damping: 25 }}
                style={{
                  flex: 1, background: card.bg, border: `1.5px dashed ${card.accent}`,
                  borderLeft: `3px solid ${card.accent}`,
                  borderRadius: 4, padding: '8px 6px', textAlign: 'center',
                  transform: `rotate(${i === 0 ? -0.5 : i === 1 ? 0.5 : -0.3}deg)`,
                }}
              >
                <p style={{ fontSize: 20, margin: '0 0 2px 0' }}>{card.emoji}</p>
                <p style={{ fontSize: i === 2 ? 16 : 24, fontWeight: 600, color: card.accent, margin: '0 0 1px 0' }}>{card.value}</p>
                <p style={{ fontSize: 11, color: '#888888', margin: 0 }}>{card.label}</p>
              </motion.div>
            ))}
          </div>

          {/* 4. Cliffhanger thought card */}
          {cliffText && (
            <div style={{
              background: '#FFFEF9', border: '1.5px dashed #CCCCCC',
              borderRadius: 4, padding: 16,
            }}>
              <p style={{ fontSize: 16, fontFamily: "'Patrick Hand', cursive", color: '#666666', margin: '0 0 6px 0' }}>
                Think about this...
              </p>
              <p style={{ fontSize: 15, color: '#333333', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                {cliffText}
              </p>
            </div>
          )}

          {/* 5. Share on WhatsApp — secondary */}
          <button
            onClick={handleWhatsApp}
            style={{
              background: 'transparent', color: '#25D366', border: '1.5px dashed #25D366', cursor: 'pointer',
              fontSize: 16, fontWeight: 400, fontFamily: "'Patrick Hand', cursive",
              padding: '10px 24px', borderRadius: 12,
              display: 'block', margin: '0 auto',
            }}
          >
            Share on WhatsApp 💚
          </button>

          {/* 6. Generate Another Story — PRIMARY action */}
          <motion.button
            whileTap={{ scale: 0.95, rotate: -1 }}
            onClick={() => { playClick(); onNext(''); }}
            style={{
              width: '100%', padding: '14px 0', borderRadius: 12, fontSize: 18,
              fontFamily: "'Patrick Hand', cursive", fontWeight: 400,
              background: '#448AFF',
              color: '#fff', border: 'none', cursor: 'pointer',
              minHeight: 52,
            }}
          >
            ✨ Generate Another Story
          </motion.button>

        </div>
      </div>
    </>
  );
};
